import { APP_COUNTRY } from 'config';
import { baseResponseSchema } from 'services/api/api.types';
import { z } from 'zod/v4';
/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

enum LimitationPeriodCode {
  DAILY = 'DD',
  WEEKLY = 'DW',
  MONTHLY = 'DM',
}

const limitationPeriodCodeSchema = z.enum([
  LimitationPeriodCode.DAILY,
  LimitationPeriodCode.WEEKLY,
  LimitationPeriodCode.MONTHLY,
]);

const currentAndNexSelfLimitationSchema = z.object({
  accountCode: z.string(),
  amount: z.number().optional(),
  typeCode: limitationPeriodCodeSchema,
  cancelTimestamp: z.number().optional(),
  cancelledBy: z.string().optional(),
  createDate: z.number().optional(),
  createdBy: z.string().optional(),
  endTimestamp: z.number().optional(),
  validityTimestamp: z.number().optional(),
  updateTimestamp: z.number().optional(),
});

const typeSchema = z.object({
  code: limitationPeriodCodeSchema,
  period: z.number(),
  latencyPeriod: z.number(),
  area: z.string(),
});

const selfLimitationItemSchema = z.object({
  currentSelfLimitation: currentAndNexSelfLimitationSchema,
  nextSelfLimitation: currentAndNexSelfLimitationSchema.optional(),
  consumedAmount: z.number(),
  type: typeSchema,
});

const topUpLimitationResponseSchema = baseResponseSchema.extend({
  responseObject: z.object({
    accountCode: z.string(),
    selfLimitationList: z.array(selfLimitationItemSchema),
  }),
});

type TopUpLimitationIResponse = z.infer<typeof topUpLimitationResponseSchema>;

type SelfLimitationItem =
  TopUpLimitationIResponse['responseObject']['selfLimitationList'][number];

type NormalizedSelfLimitationListType = Partial<
  Record<LimitationPeriodCode, SelfLimitationItem>
>;

type TransformedTopUpLimitationResponse = ReturnType<
  typeof transformTopUpLimitationResponse
>;

/*-------------------------------------------------------------------------------------------------
 * transformer helper
 * -----------------------------------------------------------------------------------------------*/

const transformTopUpLimitationResponse = (
  response: TopUpLimitationIResponse,
) => {
  const selfLimitationList = response.responseObject.selfLimitationList;

  const noLimitationsSet = selfLimitationList.length === 0;

  const normalizedSelfLimitationList: NormalizedSelfLimitationListType =
    selfLimitationList.reduce((acc, item) => {
      if (Object.values(LimitationPeriodCode).includes(item.type.code)) {
        acc[item.type.code] = item;
      }
      return acc;
    }, {});

  const dailyResidueAmount = normalizedSelfLimitationList.DD
    ? (normalizedSelfLimitationList.DD.currentSelfLimitation?.amount || 0) -
      normalizedSelfLimitationList.DD.consumedAmount
    : undefined;
  const weeklyResidueAmount = normalizedSelfLimitationList.DW
    ? (normalizedSelfLimitationList.DW.currentSelfLimitation?.amount || 0) -
      normalizedSelfLimitationList.DW.consumedAmount
    : undefined;
  const monthlyResidueAmount = normalizedSelfLimitationList.DM
    ? (normalizedSelfLimitationList.DM.currentSelfLimitation?.amount || 0) -
      normalizedSelfLimitationList.DM.consumedAmount
    : undefined;

  const isDailyLimitationReached =
    dailyResidueAmount !== undefined && dailyResidueAmount <= 0;
  const isWeeklyLimitationReached =
    weeklyResidueAmount !== undefined && weeklyResidueAmount <= 0;
  const isMonthlyLimitationReached =
    monthlyResidueAmount !== undefined && monthlyResidueAmount <= 0;

  const minResidueAmount =
    APP_COUNTRY === 'fr'
      ? (weeklyResidueAmount ?? 0)
      : Math.min(
          dailyResidueAmount ?? 0,
          weeklyResidueAmount ?? 0,
          monthlyResidueAmount ?? 0,
        );

  const isRechargeableAmount =
    (APP_COUNTRY === 'pt' && noLimitationsSet) || minResidueAmount > 0;

  const nextSelfLimitation = selfLimitationList.reduce(
    (latest: number | null, obj) => {
      const currentTimestamp = obj?.nextSelfLimitation?.validityTimestamp;
      if (!currentTimestamp) return latest;
      if (!latest || currentTimestamp > latest) {
        return currentTimestamp;
      }
      return latest;
    },
    null,
  );

  return {
    selfLimitationList: normalizedSelfLimitationList,
    noLimitationsSet,
    minResidueAmount,
    isRechargeableAmount,
    dailyResidueAmount: dailyResidueAmount
      ? dailyResidueAmount < 0
        ? 0
        : dailyResidueAmount
      : undefined,
    weeklyResidueAmount: weeklyResidueAmount
      ? weeklyResidueAmount < 0
        ? 0
        : weeklyResidueAmount
      : undefined,
    monthlyResidueAmount: monthlyResidueAmount
      ? monthlyResidueAmount < 0
        ? 0
        : monthlyResidueAmount
      : undefined,
    nextSelfLimitation,
    isDailyLimitationReached,
    isWeeklyLimitationReached,
    isMonthlyLimitationReached,
  };
};

export { topUpLimitationResponseSchema, transformTopUpLimitationResponse };
export type {
  NormalizedSelfLimitationListType,
  SelfLimitationItem,
  TopUpLimitationIResponse,
  TransformedTopUpLimitationResponse,
};
